import { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import VuiAutocomplete from "components/AutoComplete";
import OverlayLoader from "components/OverlayLoader";

import { FaExclamationCircle } from "react-icons/fa";

import useCommon from "hooks/useCommon";
import { getUsers } from "store/slice/userSlice";
import { resetUpdateRequestState } from "store/slice/requestSlice";
import dayjs from "utils/dayjs";

const statusOptions = [
  { label: "Pending", value: "pending" },
  { label: "In Progress", value: "in_progress" },
  { label: "Completed", value: "completed" },
];

const UpdateRequestDialog = ({ open, onClose, request, onUpdate }) => {
  const { STORE, DISPATCH } = useCommon();

  const { list: users, listLoading: usersLoading } = STORE((state) => state.user);
  const { updateRequestLoading, updateRequestError } = STORE((state) => state.requests);

  const hotelId = STORE((state) => state.ui.hotelId);

  const [form, setForm] = useState({
    title: "",
    description: "",
    assignedTo: [],
    dueDate: "",
    dueTime: "",
    status: "pending",
  });

  const [errors, setErrors] = useState({});

  /* ================= PREFILL ================= */
  useEffect(() => {
    if (open && request) {
      DISPATCH(getUsers({ hotelId, isOnlyActive: true, includeMe: true, includeAdmin: true }));
      DISPATCH(resetUpdateRequestState());

      setForm({
        title: request.title || "",
        description: request.description || "",
        assignedTo: request.assignedTo?.map((u) => u._id || u.id) || [],
        dueDate: request.dueDate ? dayjs.tz(request.dueDate).format("YYYY-MM-DD") : "",

        dueTime: request.dueTime || "",
        status:
          request.status === "Pending"
            ? "pending"
            : request.status === "Completed"
              ? "completed"
              : "in_progress",
      });
    }
  }, [open, request]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const newErrors = {};

    if (!form.title) newErrors.title = "Title is required";
    if (!form.description) newErrors.description = "Description is required";

    if (form.dueDate && request?.createdAt) {
      const createdAt = dayjs.tz(request.createdAt);

      if (form.dueTime) {
        const dueDateTime = dayjs.tz(`${form.dueDate}T${form.dueTime}`);

        if (dueDateTime.isBefore(createdAt)) {
          newErrors.dueDate = "Due date cannot be earlier than the request’s creation time.";
        }
      } else {
        const dueDate = dayjs.tz(form.dueDate).startOf("day");

        if (dueDate.isBefore(createdAt.startOf("day"))) {
          newErrors.dueDate = "Due date cannot be earlier than the request creation date.";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const payload = {
      requestId: request.id,
      reservationId: request.reservationId,

      title: form.title,
      description: form.description,
      assignedToUserIds: form.assignedTo,
      status: form.status,

      dueDate: form.dueDate
        ? new Date(`${form.dueDate}T${form.dueTime || "00:00"}`).toISOString()
        : null,

      dueTime: form.dueTime || null,
    };

    onUpdate?.(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (reason === "backdropClick") return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      {updateRequestLoading && <OverlayLoader open message="Updating request..." />}

      <DialogTitle>
        <VuiTypography variant="h5">Update Request</VuiTypography>
      </DialogTitle>

      <DialogContent>
        <VuiBox display="flex" flexDirection="column" gap={0.5} mt={0}>
          {/* ================= GUEST (READ ONLY) ================= */}
          <VuiBox>
            <VuiTypography variant="caption" ml={1}>
              Guest
            </VuiTypography>

            <VuiBox
              px={2}
              py={1.2}
              borderRadius="md"
              sx={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <VuiTypography variant="button" fontWeight="medium">
                {request?.guestName} {request?.room ? `• ${request.room}` : ""}
              </VuiTypography>
            </VuiBox>
          </VuiBox>

          {/* ================= TITLE ================= */}
          <VuiBox>
            <VuiTypography variant="caption" ml={1}>
              Request Title
            </VuiTypography>

            <VuiInput
              gradient
              fullWidth
              value={form.title}
              onChange={(e) => handleChange("title", e.target.value)}
              error={!!errors.title}
            />

            {errors.title && (
              <VuiTypography variant="caption" color="error">
                {errors.title}
              </VuiTypography>
            )}
          </VuiBox>

          {/* ================= DESCRIPTION ================= */}
          <VuiBox>
            <VuiTypography variant="caption" ml={1}>
              Description
            </VuiTypography>

            <VuiInput
              gradient
              fullWidth
              multiline
              rows={3}
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              error={!!errors.description}
            />

            {errors.description && (
              <VuiTypography variant="caption" color="error">
                {errors.description}
              </VuiTypography>
            )}
          </VuiBox>

          {/* ================= STATUS ================= */}
          <VuiBox>
            <VuiTypography variant="caption" ml={1} mb={0.5}>
              Status
            </VuiTypography>

            <VuiBox
              display="flex"
              p={0.5}
              borderRadius="lg"
              sx={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              {statusOptions.map((s) => {
                const isActive = form.status === s.value;

                const getStyles = () => {
                  if (!isActive) return {};

                  switch (s.value) {
                    case "pending":
                      return {
                        background: "rgba(156,163,175,0.18)",
                        border: "1px solid rgba(156,163,175,0.4)",
                      };
                    case "in_progress":
                      return {
                        background: "rgba(59,130,246,0.18)",
                        border: "1px solid rgba(59,130,246,0.5)",
                      };
                    case "completed":
                      return {
                        background: "rgba(34,197,94,0.18)",
                        border: "1px solid rgba(34,197,94,0.5)",
                      };
                    default:
                      return {};
                  }
                };

                return (
                  <VuiBox
                    key={s.value}
                    flex={1}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    py={0.8}
                    borderRadius="md"
                    sx={{
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      ...getStyles(),
                    }}
                    onClick={() => handleChange("status", s.value)}
                  >
                    <VuiTypography
                      variant="caption"
                      sx={{
                        fontWeight: isActive ? 600 : 400,
                        opacity: isActive ? 1 : 0.6,
                        fontSize: "12px",
                      }}
                    >
                      {s.label}
                    </VuiTypography>
                  </VuiBox>
                );
              })}
            </VuiBox>
          </VuiBox>

          {/* ================= ASSIGN USERS ================= */}
          <VuiBox>
            <VuiTypography variant="caption" ml={1}>
              Assign To
            </VuiTypography>

            <VuiAutocomplete
              multiple
              options={users || []}
              value={users?.filter((u) => form.assignedTo.includes(u._id))}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(opt, val) => opt._id === val._id}
              placeholder="Assign users"
              gradient
              loading={usersLoading}
              onChange={(_, selected) =>
                handleChange(
                  "assignedTo",
                  selected.map((u) => u._id)
                )
              }
            />
          </VuiBox>

          {/* ================= DUE DATE + TIME ================= */}
          <VuiBox>
            <VuiTypography variant="caption" ml={1} mb={0.5}>
              Due Date & Time
            </VuiTypography>

            <VuiBox display="flex" gap={1}>
              <VuiInput
                type="date"
                gradient
                fullWidth
                value={form.dueDate}
                onChange={(e) => handleChange("dueDate", e.target.value)}
              />

              <VuiInput
                type="time"
                gradient
                fullWidth
                value={form.dueTime}
                onChange={(e) => handleChange("dueTime", e.target.value)}
              />
            </VuiBox>
            {errors.dueDate && (
              <VuiTypography variant="caption" color="error" mt={0.5}>
                {errors.dueDate}
              </VuiTypography>
            )}
          </VuiBox>
        </VuiBox>

        {/* ================= ERROR ================= */}
        {updateRequestError && (
          <VuiBox
            display="flex"
            alignItems="center"
            gap={1}
            p={1.2}
            mb={1.5}
            mt={2}
            borderRadius="md"
            sx={{
              border: "1px solid rgba(239, 68, 68, 0.4)",
              background: "rgba(239, 68, 68, 0.08)",
            }}
          >
            <FaExclamationCircle size={14} color="#ef4444" />
            <VuiTypography variant="caption" color="error">
              {updateRequestError}
            </VuiTypography>
          </VuiBox>
        )}
      </DialogContent>

      <DialogActions>
        <VuiButton variant="outlined" color="primary" onClick={onClose}>
          Cancel
        </VuiButton>

        <VuiButton
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={updateRequestLoading}
        >
          {updateRequestLoading ? "Updating..." : "Update Request"}
        </VuiButton>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateRequestDialog;
